import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/Chart";
import { Text } from "@/components/podkit/typography/Text";
import { formatShortDate } from "@/format/time";
import { useActiveUsersTimeSeries } from "@/queries/insights-queries";
import type { TimeRange } from "@/routes/insights/insights";
import { type Timestamp } from "@bufbuild/protobuf";
import { useMemo, type FC } from "react";
import * as RechartsPrimitive from "recharts";

type PointPayload = {
    timestamp: Timestamp;
    users: number;
    returningUsers: number;
    date: Date;
};

type Props = {
    startTimestamp: Timestamp;
    endTimestamp: Timestamp;
    selectedRange: TimeRange;
};
export const ActiveUsersLineChart: FC<Props> = ({ startTimestamp, endTimestamp, selectedRange }) => {
    const { data, isLoading } = useActiveUsersTimeSeries(startTimestamp, endTimestamp, selectedRange);

    // Format data for the chart
    const chartData = useMemo(() => {
        if (!data?.weeklyActiveUsers) return [];

        return data.weeklyActiveUsers.map((point, index) => {
            const dateObj = point.time ? point.time.toDate() : new Date();
            const returningUsers = data.weeklyActiveReturningUsers?.[index]
                ? Number(data.weeklyActiveReturningUsers[index].value || 0)
                : 0;

            return {
                date: dateObj,
                timestamp: dateObj.getTime(),
                users: point.value ? Number(point.value) : 0,
                returningUsers: returningUsers,
            };
        });
    }, [data]);

    return (
        <div className="w-full">
            {isLoading ? (
                <div className="flex h-[400px] items-center justify-center">
                    <Text>Loading chart data...</Text>
                </div>
            ) : chartData.length === 0 ? (
                <div className="flex h-[400px] items-center justify-center">
                    <Text>No data available for the selected time range</Text>
                </div>
            ) : (
                <div className="size-full">
                    <ChartContainer
                        className="-mr-4 max-h-[400px] w-full"
                        config={{
                            users: {
                                label: "Active Users",
                                color: "#FF8A00",
                            },
                            returningUsers: {
                                label: "Active users w/ 2+ days",
                                color: "#18C164",
                            },
                        }}
                    >
                        <RechartsPrimitive.LineChart data={chartData}>
                            <RechartsPrimitive.CartesianGrid vertical={false} strokeDasharray="3 3" />
                            <RechartsPrimitive.XAxis
                                xAxisId="0"
                                dataKey="timestamp"
                                tickFormatter={(timestamp: number) => {
                                    const date = new Date(timestamp);
                                    return formatShortDate(date);
                                }}
                                type="number"
                                domain={["dataMin", "dataMax"]}
                                padding={{ left: 10, right: 10 }}
                                scale={"time"}
                                minTickGap={20}
                                tickCount={15}
                                tickMargin={10}
                            />
                            <RechartsPrimitive.YAxis />
                            <ChartTooltip
                                content={
                                    <ChartTooltipContent
                                        labelFormatter={(_: string | number | Date, payload: unknown[]) => {
                                            if (!payload || payload.length === 0) return "";
                                            const item = payload[0] as { payload: PointPayload };
                                            if (!(item.payload.date instanceof Date)) return "";
                                            return formatShortDate(item.payload.date, true);
                                        }}
                                    />
                                }
                            />
                            <RechartsPrimitive.Line
                                type="monotone"
                                dataKey="users"
                                name="users"
                                stroke="var(--color-users)"
                                strokeWidth={2}
                                dot={{ r: 0 }}
                                activeDot={{ r: 6 }}
                                // Use timestamp as the x-axis value
                                xAxisId="0"
                            />
                            <RechartsPrimitive.Line
                                type="monotone"
                                dataKey="returningUsers"
                                name="returningUsers"
                                stroke="var(--color-returningUsers)"
                                strokeWidth={2}
                                dot={{ r: 0 }}
                                activeDot={{ r: 6 }}
                                // Use timestamp as the x-axis value
                                xAxisId="0"
                            />
                            <ChartLegend className="justify-end" content={<ChartLegendContent />} />
                        </RechartsPrimitive.LineChart>
                    </ChartContainer>
                </div>
            )}
        </div>
    );
};
